import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowDown, PencilRuler } from "lucide-react";
import { scrollToId } from "../lib/constants";

const VERTS = [
  [-1.6, 0, -2.2], [1.6, 0, -2.2], [1.6, 0, 2.2], [-1.6, 0, 2.2],
  [-1.6, 2.3, -2.2], [1.6, 2.3, -2.2], [1.6, 2.3, 2.2], [-1.6, 2.3, 2.2],
  [0, 3.5, -2.2], [0, 3.5, 2.2],
  [-0.9, 0.8, 2.2], [0.2, 0.8, 2.2], [0.2, 1.8, 2.2], [-0.9, 1.8, 2.2],
  [0.7, 0, 2.2], [1.3, 0, 2.2], [1.3, 1.9, 2.2], [0.7, 1.9, 2.2],
];

const EDGES = [
  [0, 1], [1, 2], [2, 3], [3, 0],
  [4, 5], [5, 6], [6, 7], [7, 4],
  [0, 4], [1, 5], [2, 6], [3, 7],
  [4, 8], [5, 8], [6, 9], [7, 9],
  [10, 11], [11, 12], [12, 13], [13, 10],
  [14, 15], [15, 16], [16, 17], [17, 14],
];

const ACCENT = [[4, 8], [5, 8], [6, 9], [7, 9], [8, 9]];

const WireframeHouse = () => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext("2d");
    const mouse = { x: 0, y: 0 };
    let raf;
    let rot = 0;

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
    };
    resize();
    window.addEventListener("resize", resize);

    const onMove = (e) => {
      mouse.x = e.clientX / window.innerWidth - 0.5;
      mouse.y = e.clientY / window.innerHeight - 0.5;
    };
    window.addEventListener("mousemove", onMove);

    const draw = () => {
      rot += 0.0028;
      const ry = rot + mouse.x * 0.55;
      const rx = 0.32 + mouse.y * 0.18;
      const cyR = Math.cos(ry), syR = Math.sin(ry);
      const cxR = Math.cos(rx), sxR = Math.sin(rx);
      const w = canvas.width, h = canvas.height;
      const scale = Math.min(w, h) / 11;
      const cx = w / 2, cy = h / 2 + scale * 0.6;

      ctx.clearRect(0, 0, w, h);

      const proj = ([x, y, z]) => {
        const X = x * cyR - z * syR;
        const Z = x * syR + z * cyR;
        const Y2 = y * cxR - Z * sxR;
        return [cx + X * scale, cy - Y2 * scale];
      };

      ctx.lineWidth = Math.max(1, scale / 70);
      ctx.strokeStyle = "rgba(15,23,42,0.13)";
      for (let i = -3; i <= 3; i++) {
        ctx.beginPath();
        let [ax, ay] = proj([i * 1.6, 0, -5]);
        let [bx, by] = proj([i * 1.6, 0, 5]);
        ctx.moveTo(ax, ay); ctx.lineTo(bx, by);
        [ax, ay] = proj([-5, 0, i * 1.6]);
        [bx, by] = proj([5, 0, i * 1.6]);
        ctx.moveTo(ax, ay); ctx.lineTo(bx, by);
        ctx.stroke();
      }

      for (const [a, b] of EDGES) {
        const accent = ACCENT.some(([x, y]) => x === a && y === b);
        ctx.strokeStyle = accent ? "rgba(192,86,33,0.85)" : "rgba(15,23,42,0.5)";
        ctx.beginPath();
        const [ax, ay] = proj(VERTS[a]);
        const [bx, by] = proj(VERTS[b]);
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
        ctx.stroke();
      }

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
    };
  }, []);

  return <canvas ref={ref} className="h-full w-full" aria-hidden="true" />;
};

const LINES = ["ENGENHARIA", "QUE ERGUE", "O FUTURO."];

export const KineticHero = () => (
  <section id="topo" className="blueprint-grid relative flex min-h-screen items-center overflow-hidden pt-20">
    <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[52%] md:block">
      <WireframeHouse />
    </div>

    <div className="relative z-10 mx-auto w-full max-w-7xl px-5 py-24 sm:px-8">
      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="mb-8 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-500"
      >
        Escritório de Engenharia Civil — Terra Roxa / PR
      </motion.p>

      <h1
        data-testid="hero-title"
        className="font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight text-slate-900 sm:text-6xl lg:text-7xl"
      >
        {LINES.map((line, i) => (
          <span key={line} className="block overflow-hidden pb-1">
            <motion.span
              className={`block ${i === 2 ? "text-[#C05621]" : ""}`}
              initial={{ y: "110%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
            >
              {line}
            </motion.span>
          </span>
        ))}
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.75 }}
        className="mt-8 max-w-md text-base text-slate-600 sm:text-lg"
      >
        Projetos residenciais e comerciais sob medida para o seu terreno.
        Escopo completo, do arquitetônico ao RRT.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.9 }}
        className="mt-10 flex flex-wrap items-center gap-4"
      >
        <button
          data-testid="hero-cta-portfolio"
          onClick={() => scrollToId("#projetos")}
          className="group flex items-center gap-3 border border-slate-900 bg-slate-900 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-white transition-colors hover:border-[#C05621] hover:bg-[#C05621]"
        >
          Nossos projetos
          <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-1" />
        </button>
        <button
          data-testid="hero-cta-custom"
          onClick={() => scrollToId("#solicitar")}
          className="flex items-center gap-3 border border-slate-900/25 bg-white/60 px-7 py-4 font-mono text-xs uppercase tracking-[0.2em] text-slate-900 backdrop-blur-sm transition-colors hover:border-slate-900"
        >
          <PencilRuler size={15} />
          Solicitar projeto
        </button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.2 }}
        className="mt-16 flex flex-wrap gap-x-10 gap-y-3 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500"
      >
        <span><span className="font-bold text-slate-900">40+</span> projetos entregues</span>
        <span><span className="font-bold text-slate-900">4</span> anos de atuação</span>
        <span><span className="font-bold text-slate-900">100%</span> com RRT/ART</span>
      </motion.div>
    </div>
  </section>
);
